import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner, Table } from 'react-bootstrap';
import { toast } from 'sonner';
import { Bot } from 'lucide-react';
import SettingsCard from '../../Settings/SettingsCard';
import { useFeatures } from '../../../../context/FeatureContext';
import { getAutomationPolicy, putAutomationPolicy } from '../../../../services/ReconciliationActionService';

/**
 * Inställningar → Automatisering: what Mats may carry out on a bank line
 * without the user approving it first. A card of its own, because it governs
 * the agent only; rules and the classes of "Automatisk bokföring" govern the
 * matching engine. The two share one brake, the Nödstopp in that card.
 *
 * No switches (automation-by-entitlement SPEC D1, D9): Mats acts when the
 * package includes automatic booking and the Nödstopp is off. The card sets
 * one cap per action (empty = the product default), the kinds of proof
 * (`evidence_profiles`) Mats may rely on, and whether partial payments may be
 * registered unattended. The status line says whether he is active.
 *
 * Every save re-reads the policy first and changes one thing in it, so a
 * Nödstopp change made in the card above is never written back stale.
 * For the same reason the card does not show the Nödstopp's state itself.
 *
 * Purchases and bookings from a proposal are always approved by the user
 * (backend rule), so they have no row; their stored values are sent back as is.
 */
const ACTIONS = [
    { type: 'register_customer_payment', proof: 'invoice_reference', label: 'Registrera kundbetalning',
        when: 'När bankhändelsens referens pekar ut exakt en kundfaktura.' },
    { type: 'register_supplier_payment', proof: 'invoice_reference', label: 'Registrera leverantörsbetalning',
        when: 'När referensen pekar ut exakt en leverantörsfaktura som är bokförd som leverantörsskuld.' },
    { type: 'link_existing_payment', proof: 'recorded_payment_reference', label: 'Koppla redan registrerad betalning',
        when: 'När referensen pekar ut fakturan och exakt en registrerad betalning har samma belopp.' },
    { type: 'link_existing_verification', proof: 'existing_record_relationship', label: 'Koppla befintligt verifikat',
        when: 'När referensen pekar ut exakt ett verifikat.' },
    // Shown only with the payment_files entitlement; its stored value is sent back as is either way.
    { type: 'settle_payment_rows', proof: 'existing_record_relationship', label: 'Stäm av betalfil', feature: 'payment_files',
        when: 'När bankens återrapport (camt.054) pekar ut raderna i en betalfil som debiteringen täcker.' },
    { type: 'attach_evidence', proof: 'existing_record_relationship', label: 'Bifoga underlag', noCap: true,
        when: 'När underlaget redan hör till samma post.' },
];
const MANAGED_PROOFS = [...new Set(ACTIONS.map((a) => a.proof))];
// The evidence Mats may rely on, in the user's words (automation-by-entitlement SPEC D4).
const PROOF_LABELS = {
    invoice_reference: 'Fakturareferens pekar ut exakt en faktura',
    recorded_payment_reference: 'Referensen pekar ut en redan registrerad betalning med samma belopp',
    existing_record_relationship: 'Underlaget eller verifikatet hör redan till samma post',
};

const capText = (policy, action) => {
    const cap = policy.actions[action.type]?.cap_ore;
    return cap == null ? '' : String(cap / 100);
};
const toEdits = (policy) => Object.fromEntries(ACTIONS.map((a) => [a.type, { cap: capText(policy, a) }]));

// The document to store: caps for every action the backend knows, with `change` applied on top.
// No enabled anywhere: whether Mats acts is the package's and the Nödstopp's business.
function toRequest(policy, change = {}) {
    const rows = { ...toEdits(policy), ...change.rows };
    const actions = Object.fromEntries(Object.entries(policy.actions).map(([type, a]) => [type, { cap_ore: a.cap_ore }]));
    for (const a of ACTIONS) {
        if (!actions[a.type]) continue;
        const cap = rows[a.type].cap === '' ? null : Math.round(Number(rows[a.type].cap) * 100);
        actions[a.type] = { cap_ore: a.noCap ? null : cap };
    }
    return {
        revision: policy.revision,
        kill_switch: policy.kill_switch,
        allow_partial_payments: change.allow_partial_payments ?? policy.allow_partial_payments,
        // A proof choice is applied to the fresh document, so profiles the card does not manage survive.
        evidence_profiles: change.proof
            ? (change.checked ? [...new Set([...policy.evidence_profiles, change.proof])] : policy.evidence_profiles.filter((p) => p !== change.proof))
            : policy.evidence_profiles,
        actions,
    };
}

// One sentence on the policy's state (automation-by-entitlement SPEC D9).
export function policyStatus(policy) {
    if (!policy.enabled) return 'Ingår inte i ditt paket.';
    if (policy.kill_switch) return 'Pausad via nödstoppet.';
    return 'Mats bokför automatiskt inom gränserna nedan.';
}

const normalise = (p) => ({ ...p, evidence_profiles: Array.isArray(p.evidence_profiles) ? p.evidence_profiles : [] });

const rowStyle = { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0' };
const titleStyle = { fontSize: '0.875rem', fontWeight: 600, color: '#1f2937' };
const helpStyle = { fontSize: '0.75rem', color: '#6b7280' };

export default function AutomationPolicySettings({ companyId, canEdit = true }) {
    const { features } = useFeatures();
    const [policy, setPolicy] = useState(null);
    const [edits, setEdits] = useState({});
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(null); // 'proofs' | 'partials' | action type

    // A policy read before the default-profile backfill may lack the list.
    const show = useCallback((next) => { const p = normalise(next); setPolicy(p); setEdits(toEdits(p)); }, []);

    useEffect(() => {
        if (!companyId) return undefined;
        let active = true;
        getAutomationPolicy(companyId)
            .then((p) => { if (active) show(p); })
            // 403/404: the feature is absent for this company — no card.
            .catch((e) => { if (active && ![403, 404].includes(e.response?.status)) setError('Kunde inte läsa inställningarna för Mats.'); });
        return () => { active = false; };
    }, [companyId, show]);

    const save = async (key, change, message) => {
        setSaving(key);
        try {
            const current = normalise(await getAutomationPolicy(companyId));
            const saved = normalise(await putAutomationPolicy(companyId, toRequest(current, change)));
            // Rows with changes that are not saved yet keep them.
            const before = toEdits(policy);
            const unsaved = Object.fromEntries(Object.entries(edits).filter(([type, e]) => type !== key && e.cap !== before[type].cap));
            setPolicy(saved);
            setEdits({ ...toEdits(saved), ...unsaved });
            toast.success(message);
        } catch (e) {
            toast.error(e.response?.status === 403
                ? 'Endast företagets ägare kan ändra Mats gränser.'
                : e.response?.data?.message || 'Inställningen kunde inte sparas.');
        } finally {
            setSaving(null);
        }
    };

    if (!policy) {
        return error ? <Alert variant="warning" className="mb-0 py-2" style={{ fontSize: '0.8125rem' }}>{error}</Alert> : null;
    }

    const baseline = toEdits(policy);
    const locked = !canEdit || saving !== null;
    const setRow = (type, patch) => setEdits((prev) => ({ ...prev, [type]: { ...prev[type], ...patch } }));
    const proofs = MANAGED_PROOFS.filter((p) => PROOF_LABELS[p]);

    return (
        <SettingsCard
            icon={Bot}
            title="Mats: bankavstämning utan godkännande"
            subtitle="Mats föreslår alltid hur en bankhändelse ska hanteras. Ingår automatisk bokföring i ditt paket genomför han själv de åtgärder nedan som har bevis och ligger under beloppsgränsen; allt annat väntar på ditt godkännande i Att göra."
        >
            <div style={helpStyle}>
                Gäller bara Mats; reglerna och den automatiska bokföringen ovan är motorns. Nödstoppet ovan pausar även Mats.
                {' '}Det Mats gör själv bokförs utan användare, som motorns automatiska bokföringar.
                {!canEdit && ' Endast företagets ägare kan ändra gränserna.'}
            </div>

            <div style={{ ...rowStyle, borderBottom: '1px solid #f3f4f6', marginTop: 4 }}>
                <div style={{ flex: 1 }}>
                    <div style={titleStyle}>{policyStatus(policy)}</div>
                    <div style={helpStyle}>Tom beloppsgräns betyder standardgränsen som visas i fältet.</div>
                </div>
            </div>

            <div style={{ overflowX: 'auto' }}>
                <Table className="mb-0 align-middle" style={{ fontSize: '0.8125rem' }}>
                    <thead>
                        <tr style={{ fontSize: '0.6875rem', color: '#6b7280' }}>
                            <th style={{ paddingLeft: 0 }}>Åtgärd</th>
                            <th>Högst (kr)</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {ACTIONS.filter((a) => policy.actions[a.type] && (!a.feature || features?.[a.feature])).map((a) => {
                            const edit = edits[a.type] || baseline[a.type];
                            const stored = policy.actions[a.type];
                            const capNumber = Number(edit.cap);
                            const badCap = !a.noCap && edit.cap !== '' && (!Number.isFinite(capNumber) || capNumber <= 0);
                            const dirty = !a.noCap && edit.cap !== baseline[a.type].cap;
                            return (
                                <tr key={a.type}>
                                    <td style={{ paddingLeft: 0 }}>
                                        <div style={{ fontWeight: 500 }}>{a.label}</div>
                                        <div style={helpStyle}>{a.when}</div>
                                    </td>
                                    <td style={{ minWidth: 130 }}>
                                        {a.noCap ? <span style={helpStyle}>Inget belopp</span> : (
                                            <Form.Control
                                                type="number"
                                                size="sm"
                                                min="0"
                                                step="100"
                                                aria-label={`Beloppsgräns ${a.label}`}
                                                value={edit.cap}
                                                placeholder={stored.default_cap_ore ? String(stored.default_cap_ore / 100) : ''}
                                                isInvalid={badCap}
                                                disabled={locked}
                                                onChange={(e) => setRow(a.type, { cap: e.target.value })}
                                                style={{ maxWidth: 130 }}
                                            />
                                        )}
                                    </td>
                                    <td className="text-end" style={{ paddingRight: 0 }}>
                                        {!a.noCap && (
                                            <Button
                                                size="sm"
                                                variant="outline-primary"
                                                disabled={!dirty || badCap || locked}
                                                onClick={() => save(a.type, { rows: { [a.type]: edit } }, edit.cap === ''
                                                    ? `${a.label}: standardgränsen gäller igen.`
                                                    : `${a.label}: gränsen är sparad.`)}
                                            >
                                                {saving === a.type ? <Spinner animation="border" size="sm" /> : 'Spara'}
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            </div>

            <div style={{ ...rowStyle, borderBottom: '1px solid #f3f4f6', alignItems: 'flex-start', flexDirection: 'column', gap: 6 }}>
                <div style={titleStyle}>Bevis Mats får lita på</div>
                <div style={helpStyle}>Utan bevis väntar åtgärden på ditt godkännande, oavsett belopp.</div>
                {proofs.map((proof) => (
                    <Form.Check
                        key={proof}
                        type="checkbox"
                        id={`mats-policy-proof-${proof}`}
                        label={PROOF_LABELS[proof]}
                        checked={policy.evidence_profiles.includes(proof)}
                        disabled={locked}
                        onChange={(e) => save('proofs', { proof, checked: e.target.checked }, e.target.checked ? 'Mats får lita på beviset.' : 'Åtgärder med det beviset väntar nu på ditt godkännande.')}
                    />
                ))}
            </div>

            <div style={{ ...rowStyle, paddingBottom: 0 }}>
                <div style={{ flex: 1 }}>
                    <div style={titleStyle}>Delbetalningar</div>
                    <div style={helpStyle}>Låt Mats registrera en betalning som är mindre än fakturans belopp. Annars godkänner du dem.</div>
                </div>
                <Form.Check
                    type="switch"
                    id="mats-policy-partials"
                    aria-label="Delbetalningar utan godkännande"
                    checked={policy.allow_partial_payments}
                    disabled={locked}
                    onChange={(e) => save('partials', { allow_partial_payments: e.target.checked }, e.target.checked
                        ? 'Mats får registrera delbetalningar själv.'
                        : 'Delbetalningar kräver ditt godkännande.')}
                    label={saving === 'partials' ? <Spinner animation="border" size="sm" /> : (policy.allow_partial_payments ? 'På' : 'Av')}
                />
            </div>

            <div style={{ ...helpStyle, marginTop: 12 }}>
                Köp och bokföring från förslag godkänns alltid av dig.
            </div>
        </SettingsCard>
    );
}
